
import { ProfileForm, type ProfileFormValues } from "@/components/settings/ProfileForm";
import { ChangePasswordForm } from "@/components/settings/ChangePasswordForm";
import { useProfile } from "@/hooks/useProfile";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { useNavigate } from "react-router-dom";
import { useSession } from "@supabase/auth-helpers-react";

const Settings = () => {
  const { profile, updateProfile, isLoadingProfile } = useProfile();
  const session = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (values: ProfileFormValues) => {
    setIsLoading(true);
    try {
      await updateProfile.mutateAsync(values);
      setLastUpdated(new Date());
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while profile is being fetched
  if (isLoadingProfile) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Wait for profile data to be available before rendering the form
  if (!profile && session?.user) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando perfil...</p>
        </div>
      </div>
    );
  }

  // Use profile data if available, otherwise use auth email and empty strings
  const defaultValues: ProfileFormValues = {
    first_name: profile?.first_name || "",
    last_name: profile?.last_name || "",
    email: profile?.email || session?.user?.email || "",
    phone_number: profile?.phone_number || "",
  };

  console.log("Settings page - Profile data:", profile);
  console.log("Settings page - Default values:", defaultValues);

  return (
    <div className="min-h-screen bg-secondary">
      <div className="container max-w-2xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <h1 className="text-3xl font-semibold text-navy mb-3">
            Configuración de tu Cuenta
          </h1>
          <p className="text-lg text-coolGray">
            Personaliza tu experiencia en Migrante Amigo.
          </p>
        </div>

        {/* Profile Information Card */}
        <Card className="shadow-lg border-0 mb-8">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-navy">
              Información Personal
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            <ProfileForm
              key={`${profile?.first_name}-${profile?.last_name}-${profile?.email}-${profile?.phone_number}`}
              defaultValues={defaultValues}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              lastUpdated={lastUpdated}
            />
          </CardContent>
        </Card>

        {/* Change Password Card */}
        <Card className="shadow-lg border-0 mb-8">
          <CardHeader>
            <CardTitle className="text-xl font-semibold text-navy">
              Cambiar Contraseña
            </CardTitle>
          </CardHeader>
          <CardContent className="p-8 pt-0">
            <ChangePasswordForm />
          </CardContent>
        </Card>
        
        {/* Danger Zone */}
        <Separator className="mb-8" />
        <div className="pt-8">
          <div className="text-center mb-6">
            <h2 className="text-lg font-semibold text-destructive mb-2">
              Zona de Peligro
            </h2>
            <p className="text-sm text-coolGray mb-6">
              Esta acción eliminará permanentemente tu cuenta y todos tus datos.
            </p>
          </div>
          
          <div className="flex justify-center">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  className="w-full sm:w-auto min-h-[44px] px-6 hover:shadow-md transition-shadow"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Eliminar mi cuenta
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Estás seguro que deseas eliminar tu cuenta?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción no se puede deshacer. Perderás acceso a tus conversaciones y configuraciones.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={() => navigate('/delete-account')}
                    className="bg-destructive hover:bg-destructive/90"
                  >
                    Sí, eliminar mi cuenta
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
